/**
 * Members Page
 *
 * Browse members who have public playlists
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import './Members.css';
import UserPlaylists from '../components/UserPlaylists';

function Members({ user }) {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [error, setError] = useState(null);

  // Load members who have public playlists
  useEffect(() => {
    const loadMembers = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Query for all public playlists
        let playlistsSnapshot;
        try {
          const playlistsQuery = query(
            collection(db, 'playlists'),
            where('isPublic', '==', true)
          );

          console.log('🔍 Querying for public playlists...');
          playlistsSnapshot = await getDocs(playlistsQuery);
          console.log(`📊 Found ${playlistsSnapshot.size} public playlist(s)`);
        } catch (queryError) {
          // If query fails (e.g., missing index), try fetching all and filtering
          console.warn(
            '⚠️ Query with isPublic filter failed, trying alternative approach:',
            queryError
          );
          console.log(
            '💡 This might require a Firestore index. Check the console for index creation link.'
          );

          // Fallback: Get all playlists and filter in memory (less efficient but works)
          // Note: This will only get playlists the user can read (their own + public ones)
          const allPlaylistsQuery = query(collection(db, 'playlists'));
          const allPlaylistsSnapshot = await getDocs(allPlaylistsQuery);

          console.log(
            `📦 Fetched ${allPlaylistsSnapshot.size} total playlist(s) for filtering`
          );

          // Filter for public playlists
          const publicPlaylists = [];
          allPlaylistsSnapshot.forEach((doc) => {
            const data = doc.data();
            console.log(
              `🔍 Checking playlist ${doc.id}: isPublic=${data.isPublic}, userId=${data.userId}, currentUser=${user.uid}`
            );
            // Only include playlists that are public AND not owned by current user
            // (we want to show other users' public playlists)
            if (data.isPublic === true && data.userId !== user.uid) {
              publicPlaylists.push(doc);
              console.log(
                `✅ Added public playlist: ${data.name} by ${data.userId}`
              );
            }
          });

          // Create a mock snapshot-like object
          playlistsSnapshot = {
            size: publicPlaylists.length,
            docs: publicPlaylists,
            forEach: (callback) => publicPlaylists.forEach(callback),
          };

          console.log(
            `📊 Found ${playlistsSnapshot.size} public playlist(s) via fallback method`
          );
        }

        // Debug: Log all playlists to see what we're getting
        playlistsSnapshot.forEach((doc) => {
          const data = doc.data();
          console.log('📋 Playlist:', {
            id: doc.id,
            name: data.name,
            userId: data.userId,
            currentUserId: user.uid,
            isPublic: data.isPublic,
            isPublicType: typeof data.isPublic,
            isOwnPlaylist: data.userId === user.uid,
          });
        });

        // Get unique user IDs from public playlists
        // Exclude current user's playlists (we want to show other members)
        const userIds = new Set();
        let totalPublicPlaylists = 0;
        let ownPublicPlaylists = 0;

        console.log(`🔍 Filtering playlists - Current user ID: ${user.uid}`);

        playlistsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.userId && data.isPublic === true) {
            totalPublicPlaylists++;
            if (data.userId === user.uid) {
              ownPublicPlaylists++;
              console.log(
                `❌ Skipping own playlist: ${data.name} (userId: ${data.userId} === currentUser: ${user.uid})`
              );
            } else {
              userIds.add(data.userId);
              console.log(
                `✅ Added user ${data.userId} to members list (currentUser: ${user.uid})`
              );
            }
          }
        });

        console.log(
          `📊 Public playlists: ${totalPublicPlaylists} total, ${ownPublicPlaylists} own, ${userIds.size} from other members`
        );

        // Fetch user documents for each unique user ID
        const membersData = [];
        console.log(
          `📥 Fetching user documents for ${userIds.size} user(s)...`
        );

        // Safety check: Ensure we never include the current user
        if (userIds.has(user.uid)) {
          console.warn(
            `⚠️ WARNING: Current user ${user.uid} was in userIds Set! Removing...`
          );
          userIds.delete(user.uid);
        }

        for (const userId of userIds) {
          // Double-check: Skip if this is the current user
          if (userId === user.uid) {
            console.warn(`⚠️ Skipping current user: ${userId}`);
            continue;
          }
          try {
            console.log(`🔍 Fetching user document for: ${userId}`);
            const userDoc = await getDoc(doc(db, 'users', userId));
            console.log(
              `📄 User document exists: ${userDoc.exists()} for ${userId}`
            );
            if (userDoc.exists()) {
              const userData = userDoc.data();
              console.log(`✅ User data loaded:`, {
                userId,
                email: userData.email,
                displayName: userData.displayName,
              });

              // Count public playlists for this user
              const userPublicPlaylists = playlistsSnapshot.docs.filter(
                (doc) => doc.data().userId === userId
              );

              console.log(
                `📊 User has ${userPublicPlaylists.length} public playlist(s)`
              );

              membersData.push({
                id: userId,
                email: userData.email || '',
                displayName:
                  userData.displayName || userData.email || 'Unknown User',
                photoURL: userData.photoURL || null,
                publicPlaylistCount: userPublicPlaylists.length,
              });

              console.log(
                `✅ Added member to list: ${
                  userData.displayName || userData.email
                }`
              );
            } else {
              console.warn(
                `⚠️ User document does not exist for userId: ${userId}. Using playlist data as fallback.`
              );

              // Fallback: Use email from playlist data if available
              const userPublicPlaylists = playlistsSnapshot.docs.filter(
                (doc) => doc.data().userId === userId
              );

              if (userPublicPlaylists.length > 0) {
                // Get email from first playlist (playlists have userEmail field)
                const firstPlaylist = userPublicPlaylists[0].data();
                const userEmail = firstPlaylist.userEmail || '';

                membersData.push({
                  id: userId,
                  email: userEmail,
                  displayName: userEmail || 'Unknown User',
                  photoURL: null,
                  publicPlaylistCount: userPublicPlaylists.length,
                });

                console.log(
                  `✅ Added member using fallback data: ${userEmail}`
                );
              }
            }
          } catch (err) {
            console.error(`❌ Error loading user ${userId}:`, err);
          }
        }

        console.log(
          `👥 Final members data: ${membersData.length} member(s)`,
          membersData
        );

        // Sort by display name
        membersData.sort((a, b) => a.displayName.localeCompare(b.displayName));

        setMembers(membersData);

        // If no members found, log additional debug info
        if (membersData.length === 0) {
          console.log(
            'ℹ️ No OTHER members with public playlists found. (Your own playlists are excluded)'
          );
          if (
            totalPublicPlaylists > 0 &&
            ownPublicPlaylists === totalPublicPlaylists
          ) {
            console.log(
              `   You have ${ownPublicPlaylists} public playlist(s), but the Members page shows OTHER members only.`
            );
          }
        }
      } catch (err) {
        console.error('❌ Error loading members:', err);
        console.error('Error details:', {
          code: err.code,
          message: err.message,
          stack: err.stack,
        });

        // Check if it's an index error
        if (
          err.code === 'failed-precondition' ||
          err.message?.includes('index')
        ) {
          setError(
            `Firestore index required. Check the console for the index creation link, or use the fallback query method.`
          );
        } else {
          setError(err.message || 'Failed to load members');
        }
      } finally {
        setLoading(false);
      }
    };

    loadMembers();
  }, [user]);

  const handleMemberSelect = (member) => {
    setSelectedMember(member);
  };

  const handleBack = () => {
    setSelectedMember(null);
  };

  const handleLoadPlaylist = (playlist) => {
    // Store playlist in sessionStorage so LibraryLayout can load it
    sessionStorage.setItem('loadPlaylistFromMembers', JSON.stringify(playlist));
    // Navigate to library page
    navigate('/library');
  };

  if (loading) {
    return (
      <div className="members-page">
        <div className="members-header">
          <h1>👥 Members</h1>
        </div>
        <div className="members-loading">
          <div className="loading-spinner"></div>
          <p>Loading members...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="members-page">
        <div className="members-header">
          <h1>👥 Members</h1>
        </div>
        <div className="members-error">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  // If a member is selected, show their playlists
  if (selectedMember) {
    return (
      <UserPlaylists
        member={selectedMember}
        onBack={handleBack}
        currentUser={user}
        onLoadPlaylist={handleLoadPlaylist}
      />
    );
  }

  return (
    <div className="members-page">
      <div className="members-header">
        <h1>👥 Members</h1>
        <p className="members-subtitle">
          Discover playlists shared by other members
        </p>
      </div>

      {members.length === 0 ? (
        <div className="members-empty">
          <div className="empty-icon">🎵</div>
          <h2>No other members with public playlists yet</h2>
          <p>
            The Members page shows playlists shared by{' '}
            <strong>other members</strong>. Your own public playlists are not
            shown here.
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#aaa' }}>
            To see other members, they need to mark their playlists as public.
          </p>
        </div>
      ) : (
        <div className="members-grid">
          {members.map((member) => (
            <div
              key={member.id}
              className="member-card"
              onClick={() => handleMemberSelect(member)}
            >
              <div className="member-avatar">
                {member.photoURL ? (
                  <img src={member.photoURL} alt={member.displayName} />
                ) : (
                  <div className="member-avatar-placeholder">
                    {member.displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="member-info">
                <h3 className="member-name">{member.displayName}</h3>
                <p className="member-email">{member.email}</p>
                <p className="member-playlist-count">
                  📋 {member.publicPlaylistCount} public playlist
                  {member.publicPlaylistCount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Members;
